import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { Loader2, Flame, Trophy, Target, Calendar, TrendingUp } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { GymHeader } from "@/components/GymHeader";

export default function MyStats() {
  const { data: stats, isLoading } = trpc.gym.gamification.myStats.useQuery(undefined);

  if (isLoading) {
    return (
      <>
        <GymHeader />
        <div className="container py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </>
    );
  }

  if (!stats) {
    return (
      <>
        <GymHeader />
        <div className="container py-8">
          <div className="text-center py-12">
            <p className="text-muted-foreground">Keine Statistiken verfügbar</p>
          </div>
        </div>
      </>
    );
  }

  const earnedBadges = stats.badges.filter(b => b.earned);
  const nextBadge = stats.badges.find(b => !b.earned);

  // Get motivational message based on current streak
  const getMotivationalMessage = () => {
    if (stats.currentStreak === 0) {
      return "Starten Sie Ihre Fitness-Reise heute! 💪";
    } else if (stats.currentStreak < 3) {
      return "Großartig! Bleiben Sie dran! 🎯";
    } else if (stats.currentStreak < 7) {
      return "Sie sind auf einem guten Weg! 🔥";
    } else if (stats.currentStreak < 14) {
      return "Unglaublich! Sie sind eine Inspiration! ⭐";
    } else if (stats.currentStreak < 30) {
      return "Phänomenal! Sie sind ein Vorbild! 🏆";
    } else {
      return "Legendär! Sie sind unaufhaltsam! 👑";
    }
  };

  return (
    <>
      <GymHeader />
      <div className="container py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Meine Statistiken</h1>
          <p className="text-muted-foreground mt-2">
            {getMotivationalMessage()}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Aktuelle Serie</CardTitle>
              <Flame className="h-5 w-5 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.currentStreak}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {stats.currentStreak === 1 ? "Tag" : "Tage"} in Folge
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-primary">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Längste Serie</CardTitle>
              <TrendingUp className="h-5 w-5 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.longestStreak}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Ihr persönlicher Rekord
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Gesamt Check-ins</CardTitle>
              <Target className="h-5 w-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.totalCheckIns}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Trainingseinheiten absolviert
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Badges Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              Errungenschaften
            </CardTitle>
            <CardDescription>
              {earnedBadges.length} von {stats.badges.length} Abzeichen freigeschaltet
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {stats.badges.map((badge) => (
                <div
                  key={badge.id}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    badge.earned
                      ? "border-primary bg-primary/5"
                      : "border-muted bg-muted/20 opacity-60"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-3xl">{badge.icon}</div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{badge.name}</h3>
                      <p className="text-sm text-muted-foreground">{badge.description}</p>
                      
                      {!badge.earned && badge.progress !== undefined && badge.target !== undefined && (
                        <div className="mt-3 space-y-1">
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>Fortschritt</span>
                            <span>{badge.progress} / {badge.target}</span>
                          </div>
                          <Progress value={(badge.progress / badge.target) * 100} className="h-2" />
                        </div>
                      )}
                      
                      {badge.earned && (
                        <Badge variant="secondary" className="mt-2">
                          Freigeschaltet ✓
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Next Goal */}
        {nextBadge && (
          <Card className="border-2 border-dashed border-primary/50 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-primary">
                <Target className="h-5 w-5" />
                Nächstes Ziel
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="text-4xl">{nextBadge.icon}</div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{nextBadge.name}</h3>
                  <p className="text-muted-foreground">{nextBadge.description}</p>
                  {nextBadge.progress !== undefined && nextBadge.target !== undefined && (
                    <div className="mt-3 space-y-2">
                      <Progress value={(nextBadge.progress / nextBadge.target) * 100} className="h-3" />
                      <p className="text-sm text-muted-foreground">
                        Noch <span className="font-semibold text-primary">{nextBadge.target - nextBadge.progress}</span> bis zum Ziel!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Check-ins */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Letzte Check-ins
            </CardTitle>
            <CardDescription>
              Ihre letzten Trainingseinheiten
            </CardDescription>
          </CardHeader>
          <CardContent>
            {stats.recentCheckIns.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Noch keine Check-ins vorhanden
              </p>
            ) : (
              <div className="space-y-3">
                {stats.recentCheckIns.map((checkIn, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
                        <Calendar className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-medium">{checkIn.serviceName}</p>
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(checkIn.date), "EEEE, d. MMMM yyyy", { locale: de })}
                        </p>
                      </div>
                    </div>
                    <Badge variant="secondary">✓</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
